<script>
  import { inertia, router, page } from '@inertiajs/svelte';
  import Icon from '@iconify/svelte';

  export let title = 'Default Title';
  export let subtitle = null;

  $: currentUser = $page.props.current_user;

  let isMenuOpen = false;
  let isEditorOpen = false;
  let isJumpToOpen = false;

  function toggleMenu() { isMenuOpen = !isMenuOpen; }
  function toggleEditor() { isEditorOpen = !isEditorOpen; }
  function toggleJumpTo() { isJumpToOpen = !isJumpToOpen; }

  function logout() {
    router.delete('/logout');
  }

  $: document.title = title;

  const editorGroups = [
    {
      label: 'World',
      items: ['locations', 'sublocations', 'buildings', 'shops'],
    },
    {
      label: 'Characters',
      items: ['species', 'person_classes', 'attributes', 'skills'],
    },
    {
      label: 'Jobs & Effects',
      items: ['jobs', 'effects', 'items', 'traits'],
    },
    {
      label: 'Events',
      items: ['events', 'crew_events', 'quest_chains'],
    },
    {
      label: 'Misc',
      items: ['songs', 'name_lists'],
    },
  ];

  const encyclopediaPath = '/encyclopedia';

  function typeLabel(t) {
    return t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }
</script>

<header>
  <nav>
    <div class="logo">
      <a use:inertia href="/">
        <Icon icon="mdi:home" />
        Age of Trevor Webedit
      </a>
    </div>

    <button class="menu-toggle" on:click={toggleMenu} aria-label="Toggle menu">
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="24" height="24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
      </svg>
    </button>

    <ul class={`menu ${isMenuOpen ? 'open' : ''}`}>
      <li>
        <button class="dropdown-toggle" on:click={toggleEditor}>
          <Icon icon="mdi:file-edit-outline" />
          Editors
          <Icon icon={isEditorOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'} />
        </button>
        {#if isEditorOpen}
          <div class="dropdown">
            {#each editorGroups as group}
              <div class="dropdown-group">
                <span class="dropdown-group-label">{group.label}</span>
                {#each group.items as item}
                  <a use:inertia href="/editor/{item}" class="dropdown-item"
                     on:click={() => { isEditorOpen = false; isMenuOpen = false; }}>
                    {typeLabel(item)}
                  </a>
                {/each}
              </div>
            {/each}
          </div>
        {/if}
      </li>

      <li>
        <a use:inertia href={encyclopediaPath} class="nav-link">
          <Icon icon="mdi:book-open-outline" />
          Encyclopedia
        </a>
      </li>

      <li>
        <a use:inertia href="/images" class="nav-link">
          <Icon icon="mdi:image-multiple-outline" />
          Images
        </a>
      </li>

      <li>
        <button class="dropdown-toggle" on:click={toggleJumpTo}>
          <Icon icon="mdi:map-marker-path" />
          Jump-to
          <Icon icon={isJumpToOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'} />
        </button>
        {#if isJumpToOpen}
          <div class="dropdown jump-to-dropdown">
            <a href="/random/event" class="dropdown-item"
               on:click={() => { isJumpToOpen = false; isMenuOpen = false; }}>
              <Icon icon="mdi:dice-multiple-outline" />
              Random Event
            </a>
            <a href="/random/encyclopedia" class="dropdown-item"
               on:click={() => { isJumpToOpen = false; isMenuOpen = false; }}>
              <Icon icon="mdi:shuffle-variant" />
              Random Encyc
            </a>
            <a href="/random/oldest_modified" class="dropdown-item"
               on:click={() => { isJumpToOpen = false; isMenuOpen = false; }}>
              <Icon icon="mdi:clock-outline" />
              Oldest Modified
            </a>
          </div>
        {/if}
      </li>

      {#if currentUser}
        <li class="user-info">
          <span class="user-email"><Icon icon="mdi:account-circle" /> {currentUser.email}</span>
          <button class="btn-logout" on:click={logout}>Sign out</button>
        </li>
      {/if}
    </ul>
  </nav>
</header>

<main>
  <div class="container">
    <h1>{title}</h1>
    {#if subtitle}
      <p class="subtitle">{subtitle}</p>
    {/if}
    <slot></slot>
  </div>
</main>

<style>
  header {
    background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 50%, #1e3a8a 100%);
    color: white;
    padding: 0 2rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    position: relative;
    z-index: 100;
  }

  nav {
    display: flex;
    justify-content: space-between;
    align-items: stretch;
    min-height: 60px;
  }

  .logo {
    display: flex;
    align-items: center;
    font-size: 1.2rem;
    font-weight: 700;
  }

  .logo a {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: white;
    text-decoration: none;
    padding: 0.5rem;
    border-radius: 0.5rem;
    transition: background 0.2s;
  }

  .logo a:hover { background: rgba(255,255,255,0.1); }

  .menu-toggle {
    display: none;
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    padding: 0.5rem;
    align-self: center;
  }

  .menu {
    display: flex;
    list-style: none;
    margin: 0;
    padding: 0;
    align-items: stretch;
    gap: 0.25rem;
  }

  .menu > li {
    display: flex;
    align-items: stretch;
    position: relative;
  }

  .dropdown-toggle {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    background: none;
    border: none;
    color: white;
    font-size: 0.9rem;
    font-weight: 500;
    padding: 0 1rem;
    cursor: pointer;
    border-radius: 0;
    transition: background 0.2s;
    white-space: nowrap;
  }

  .dropdown-toggle:hover { background: rgba(255,255,255,0.1); }

  .nav-link {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    color: white;
    text-decoration: none;
    font-size: 0.9rem;
    font-weight: 500;
    padding: 0 1rem;
    transition: background 0.2s;
    border-radius: 0;
    white-space: nowrap;
  }

  .nav-link:hover { background: rgba(255,255,255,0.1); }

  .dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    background: white;
    border-radius: 0.5rem;
    box-shadow: 0 10px 25px rgba(0,0,0,0.15);
    padding: 0.5rem;
    min-width: 600px;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.25rem 1rem;
    z-index: 200;
  }

  .jump-to-dropdown {
    min-width: 180px;
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
  }

  .dropdown-group {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    padding: 0.25rem 0;
  }

  .dropdown-group-label {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #94a3b8;
    padding: 0.375rem 0.5rem 0.25rem;
  }

  .dropdown-item {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.4rem 0.5rem;
    font-size: 0.85rem;
    color: #1e293b;
    text-decoration: none;
    border-radius: 0.375rem;
    transition: background 0.15s;
  }

  .dropdown-item:hover {
    background: #eff6ff;
    color: #1d4ed8;
  }

  .user-info {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0 0.5rem 0 1rem;
    margin-left: 0.5rem;
    border-left: 1px solid rgba(255,255,255,0.2);
  }

  .user-email {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.8rem;
    color: rgba(255,255,255,0.8);
    white-space: nowrap;
  }

  .btn-logout {
    background: rgba(255,255,255,0.15);
    border: 1px solid rgba(255,255,255,0.3);
    color: white;
    font-size: 0.8rem;
    padding: 0.3rem 0.75rem;
    border-radius: 0.375rem;
    cursor: pointer;
    transition: background 0.2s;
    white-space: nowrap;
  }

  .btn-logout:hover { background: rgba(255,255,255,0.25); }

  main {
    background: #f8fafc;
    min-height: calc(100vh - 60px);
    padding: 2.5rem 2rem;
  }

  .container {
    max-width: 1200px;
    margin: 0 auto;
  }

  h1 {
    font-size: 2rem;
    font-weight: 800;
    color: #1e3a8a;
    margin: 0 0 1.5rem 0;
    letter-spacing: -0.03em;
  }

  .subtitle {
    font-size: 1.1rem;
    color: #64748b;
    margin: -1rem 0 1.5rem 0;
  }

  @media (max-width: 768px) {
    header { padding: 0 1rem; }

    .menu-toggle { display: flex; }

    .menu {
      display: none;
      flex-direction: column;
      align-items: stretch;
      position: absolute;
      top: 60px;
      left: 0;
      right: 0;
      background: #1e3a8a;
      padding: 1rem;
      box-shadow: 0 10px 15px rgba(0,0,0,0.1);
    }

    .menu.open { display: flex; }

    .dropdown {
      position: static;
      min-width: unset;
      grid-template-columns: repeat(2, 1fr);
      background: rgba(255,255,255,0.05);
      box-shadow: none;
      border-radius: 0.5rem;
      margin-top: 0.25rem;
    }

    .dropdown-group-label { color: rgba(255,255,255,0.5); }

    .dropdown-item { color: rgba(255,255,255,0.85); }
    .dropdown-item:hover { background: rgba(255,255,255,0.1); color: white; }

    .user-info {
      flex-direction: column;
      align-items: flex-start;
      border-left: none;
      border-top: 1px solid rgba(255,255,255,0.2);
      padding: 0.75rem 0.5rem 0.25rem;
      margin-left: 0;
      gap: 0.5rem;
    }

    main { padding: 2rem 1rem; }
    h1 { font-size: 1.5rem; }
  }
</style>
